'use client';

import { Stack, Tooltip, Typography } from '@mui/material';
import type { GcpSqlVmBackupSettings } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { MainDialog } from '@/components/dialogs/mainDialog';
import { FormTextField } from '@/components/form/formTextField';
import { useDAL } from '@/contexts/useDAL';
import { useWorkspace } from '@/contexts/useWorkspace';
import { formOf, type GcpSqlVmBackupSettingsForm, isFormValid, partSizeDisabledReason, SETUP_DEFAULTS, settingsValidation, updateOf } from './gcpSqlVmBackupSettings';
export const GcpSqlVmBackupSettingsDialog = ({
  resourceId,
  settings,
  onClose,
  onSaved
}: {
  resourceId: string;
  settings: GcpSqlVmBackupSettings | undefined;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) => {
  const dal = useDAL();
  const {
    snackbar
  } = useWorkspace();
  const [form, setForm] = useState<GcpSqlVmBackupSettingsForm>(() => formOf(settings));
  const [isSaving, setIsSaving] = useState(false);
  const {
    data: setup,
    isLoading: isSetupLoading
  } = dal.gcpSqlVm.useGcpSqlVmSetup(resourceId);
  const partSizeDisabled = partSizeDisabledReason(setup?.setupPath);
  const setField = (key: keyof GcpSqlVmBackupSettingsForm) => (event: React.ChangeEvent<HTMLInputElement>) => setForm(current => ({
    ...current,
    [key]: event.target.value.trim()
  }));
  const save = async () => {
    setIsSaving(true);
    try {
      await dal.inventory.configuration.update(resourceId, {
        setGcpSqlVmBackupSettings: updateOf(form, {
          includePartSize: !partSizeDisabled
        })
      });
      await onSaved();
      snackbar.showMessage('Backup settings saved. The VM applies them on its next backup.');
      onClose();
    } catch (error) {
      snackbar.showErrorMessage(await extractErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };
  return <MainDialog isOpen={true} isLoading={isSetupLoading} header={{
    title: 'Backup settings',
    subtitle: 'Leave a field empty to use the value from setup.',
    onClose
  }} footer={{
    primaryButton: {
      text: 'Save',
      onClick: () => void save(),
      disabled: !isFormValid(form, {
        includePartSize: !partSizeDisabled
      }) || isSaving,
      'data-testid': 'gcp-sql-vm-backup-settings-save-button'
    },
    secondaryButton: {
      text: 'Cancel',
      onClick: onClose,
      'data-testid': 'gcp-sql-vm-backup-settings-cancel-button'
    },
    submitInProgress: isSaving,
    loadingText: 'Saving...'
  }}>
      <Stack sx={{
      paddingX: '40px',
      gap: '24px'
    }}>
        <FormTextField title='Transaction log backup interval (minutes)' helpText="How often SQL Server backs up each database's transaction log. 1-10, default 5." alwaysShowHelp placeholder={String(SETUP_DEFAULTS.logBackupIntervalMinutes)} value={form.logBackupIntervalMinutes} onChange={setField('logBackupIntervalMinutes')} validation={settingsValidation.logBackupIntervalMinutes} dataTestId='gcp-sql-vm-backup-settings-log-interval' />
        <FormTextField title='Daily full backup time (VM local time)' helpText="When SQL Server starts the daily full backup of each database, in the VM's time zone (HH:mm:ss). Default 01:00:00." alwaysShowHelp placeholder={SETUP_DEFAULTS.fullBackupTime} value={form.fullBackupTime} onChange={setField('fullBackupTime')} validation={settingsValidation.fullBackupTime} dataTestId='gcp-sql-vm-backup-settings-full-time' />
        <Tooltip title={partSizeDisabled ?? ''} placement='top'>
          <div>
            <FormTextField title='Upload part size (MiB)' helpText='Size of each chunk SQL Server uploads during a backup; smaller chunks use less memory. A backup file can hold only 10,000 chunks, so large databases need a larger size.' alwaysShowHelp disabled={!!partSizeDisabled} placeholder={String(SETUP_DEFAULTS.maxTransferSizeMiB)} value={form.maxTransferSizeMiB} onChange={setField('maxTransferSizeMiB')} validation={settingsValidation.maxTransferSizeMiB} dataTestId='gcp-sql-vm-backup-settings-part-size' />
          </div>
        </Tooltip>
        <Typography variant='body2'>
          Changes reach the VM within about 20 minutes and apply from its next
          backup run.
        </Typography>
      </Stack>
    </MainDialog>;
};